import { useState, useEffect, useCallback } from "react";
import { API } from "../App";
import { toast } from "sonner";
import { User, RefreshCw, Check, Filter } from "lucide-react";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import axios from "axios";
import Layout from "../components/Layout";
import { TicketCard } from "../components/tickets/TicketCard";


const TicketsPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState("all"); // Show all inbox tickets by default
  const [userTypeFilter, setUserTypeFilter] = useState("all"); // all, manager, provider

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/hubspot/tickets`, {
        withCredentials: true
      });
      setTickets(response.data.tickets || []);
    } catch (error) {
      // Error handled silently
      toast.error("Failed to fetch tickets from HubSpot");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // Filter by status and user type locally
  const filteredTickets = tickets.filter((ticket) => {
    // Status filter
    if (filter !== "all") {
      const stage = ticket.status;
      if (filter === "new" && stage !== "1") return false;
      if (filter === "waiting_contact" && stage !== "2") return false;
      if (filter === "waiting_us" && stage !== "3") return false;
    }
    // User type filter
    if (userTypeFilter !== "all") {
      const userType = ticket.user_type?.toLowerCase();
      if (userTypeFilter === "manager" && userType !== "manager") return false;
      if (userTypeFilter === "provider" && userType !== "provider") return false;
    }
    return true;
  });

  const openCount = tickets.filter(t => {
    const s = t.status;
    return s === "1" || s === "2" || s === "3"; // All non-closed stages
  }).length;

  const newCount = tickets.filter(t => t.status === "1").length;
  const waitingContactCount = tickets.filter(t => t.status === "2").length;
  const waitingUsCount = tickets.filter(t => t.status === "3").length;

  const managerCount = tickets.filter(t => t.user_type?.toLowerCase() === "manager").length;
  const providerCount = tickets.filter(t => t.user_type?.toLowerCase() === "provider").length;

  return (
    <Layout title="Open Tickets">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <p className="text-slate-500">
              {filteredTickets.length} tickets • {newCount} new, {waitingContactCount} waiting on contact, {waitingUsCount} waiting on us
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Status Filter */}
            <div className="flex bg-white rounded-lg border border-slate-200 p-1 shadow-sm">
              {[
                { key: "all", label: "All Open" },
                { key: "new", label: "New" },
                { key: "waiting_contact", label: "Waiting on Contact" },
                { key: "waiting_us", label: "Waiting on Us" }
              ].map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  data-testid={`filter-${f.key}`}
                  className={`px-4 py-2 text-sm rounded-md transition-all ${
                    filter === f.key
                      ? "bg-[#425B76] text-white shadow-sm"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            
            {/* User Type Filter */}
            <Select value={userTypeFilter} onValueChange={setUserTypeFilter}>
              <SelectTrigger className="w-[160px] bg-white border-slate-200">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-400" />
                  <SelectValue placeholder="User Type" />
                </div>
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Users</SelectItem>
                <SelectItem value="manager">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                    Managers ({managerCount})
                  </span>
                </SelectItem>
                <SelectItem value="provider">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-teal-500"></span>
                    Providers ({providerCount})
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
            
            <Button
              onClick={fetchTickets}
              variant="outline"
              className="border-slate-200 text-slate-600 hover:bg-slate-50"
              data-testid="refresh-tickets"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {/* Tickets List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#425B76] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredTickets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mb-4">
              <Check className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">All caught up!</h3>
            <p className="text-slate-500">No tickets match your current filter.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="tickets-list">
            {filteredTickets.map((ticket) => (
              <TicketCard
                key={ticket.ticket_id}
                ticket={ticket}
                isExpanded={expandedId === ticket.ticket_id}
                onToggle={() =>
                  setExpandedId(expandedId === ticket.ticket_id ? null : ticket.ticket_id)
                }
              />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TicketsPage;
